import React, { useMemo, useState } from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { exhibitions } from "../../../../services/TicketsServices";
import { messages } from "../../../../common/Models/Validations";
import useExhibitions from "../shared/useExhibitions";
import SimpleConfigTable from "../shared/SimpleConfigTable";
import ConfirmDeleteDialog from "../shared/ConfirmDeleteDialog";
import TablePagerBar from "../shared/TablePagerBar";
import ExhibitionFormDialog from "../shared/ExhibitionFormDialog";

export default function ExhibitionsPanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const {
    items,
    activeFilter,
    pagination,
    deleteTarget,
    deleteLoading,
    fetchList,
    changePage,
    changePerPage,
    changeActiveFilter,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useExhibitions();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        museum_id: Yup.string().required(messages.required),
        start_date: Yup.string().required(messages.required),
        end_date: Yup.string().required(messages.required),
        name: Yup.string().required(messages.required),
        description: Yup.string().required(messages.required),
      }),
    []
  );

  const columns = useMemo(
    () => [
      { key: "museum", label: "Museum" },
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
      { key: "days", label: "Days" },
      { key: "is_active_label", label: "Active" },
    ],
    []
  );

  const toDay = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const getDaysInclusive = (start, end) => {
    const s = toDay(start);
    const e = toDay(end);
    if (!s || !e) return "-";
    const diff = Math.round((e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000));
    return diff >= 0 ? String(diff + 1) : "-";
  };

  const handleAdd = () => {
    setEditRow(null);
    setOpen(true);
  };

  const handleEdit = (row) => {
    setEditRow(row?.original || row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditRow(null);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      museum_id: Number(values.museum_id),
      is_active: values.is_active,
      start_date: values.start_date,
      end_date: values.end_date,
      translations: {
        en: {
          name: values.name,
          description: values.description,
        },
      },
    };

    try {
      if (editRow?.id) {
        await exhibitions("put", payload, null, editRow.id);
      } else {
        await exhibitions("post", payload);
      }
      await fetchList(pagination.page, pagination.perPage, activeFilter);
      handleClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save exhibition:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const rows = useMemo(() => {
    return items.map((it) => ({
      id: it.id,
      museum: it.museum_name || it.museum_id || "-",
      name: it?.translations?.en?.name || it.name || "-",
      description: it?.translations?.en?.description || it.description || "-",
      days: getDaysInclusive(it.start_date, it.end_date),
      is_active_label: it.is_active ? "Yes" : "No",
      original: it,
    }));
  }, [items]);

  const handleRowClick = (row) => {
    if (!row?.id) return;
    navigate(`/management/ticket-management/exhibitions/${row.id}`);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Exhibitions</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Active</InputLabel>
            <Select
              value={activeFilter}
              label="Active"
              onChange={(e) => changeActiveFilter(e.target.value)}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"active"}>Active</MenuItem>
              <MenuItem value={"inactive"}>Inactive</MenuItem>
            </Select>
          </FormControl>
          <Button variant={"contained"} onClick={handleAdd} className={"text-capitalize"}>
            Add Exhibition
          </Button>
        </Box>
      </Box>

      <SimpleConfigTable
        columns={columns}
        rows={rows}
        onEdit={handleEdit}
        onLogicalDelete={requestDelete}
        onRowClick={handleRowClick}
      />

      <TablePagerBar
        perPage={pagination.perPage}
        onPerPageChange={(e) => changePerPage(Number(e.target.value))}
        total={pagination.total}
        page={pagination.page}
        onPageChange={(_, page) => changePage(page)}
      />

      {open && (
        <ExhibitionFormDialog
          open={open}
          onClose={handleClose}
          editingItem={editRow}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        />
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title={"Delete exhibition"}
        description={"Are you sure you want to delete this exhibition?"}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </Box>
  );
}
